import { Controller } from '@hotwired/stimulus';
import { getMeta } from '../helpers';

export default class extends Controller {
    static targets = ['trigger'];

    initialize() {
        super.initialize();

        this.mouseDown = (event) => this.dragStart(event);
        this.mouseUp = (event) => this.dragEnd(event);
        this.mouseMove = (event) => this.dragMove(event);
        this.mouseOver = (event) => this.dragOver(event);
        this.mouseOverDropZone = (event) => this.dragOverDropzone(event);

        this.dropTarget = null;
        this.dropMode = null;
        this.dropUrl = null;
        this.dropEntryID = null;
        this.dropActionLoading = false;

        this.placeholder = null;
        this.swapper = null;
        this.animated = true;
    }

    connect() {
        this.dropUrl = this.element.dataset.reorderUrl;
        this.dropEntryID = this.element.dataset.entryId;
    }

    triggerTargetConnected(element) {
        this.element.dataset.dragging = 'false';
        element.addEventListener('mousedown', this.mouseDown);
        this.element.addEventListener('mouseover', this.mouseOver);
    }

    triggerTargetDisconnected(element) {
        this.element.dataset.dragging = 'false';
        element.removeEventListener('mousedown', this.mouseDown);
        this.element.removeEventListener('mouseover', this.mouseOver);
    }

    dragStart(event) {
        this.removeTemporaryElements();

        if (event.button === 0 && this.element.dataset.dragging === 'false') {
            event.stopImmediatePropagation();

            document.body.classList.add('dnd-block-user-select');
            document.addEventListener('mouseup', this.mouseUp);
            document.addEventListener('mousemove', this.mouseMove);
            this.element.parentNode.addEventListener('dnd:notify-dragged-over-dropzone', this.mouseOverDropZone);

            this.placeholder = this.makePlaceholder(event);
            this.element.dataset.dragging = 'true';
            this.swapper = this.makeSwapper();

            this.element.parentNode.insertBefore(this.swapper, this.element.nextElementSibling);
            this.element.parentNode.appendChild(this.placeholder);
            this.element.dispatchEvent(new CustomEvent('dnd:start', { bubbles: true }));
        }
    }

    dragEnd() {
        this.dropActionLoading = true;

        const url = this.dropUrl;
        const fromID = this.dropEntryID;
        const toID = this.dropTarget?.dataset?.entryId;
        const mode = this.dropMode;
        const fromElement = this.element;
        const toElement = this.dropTarget;

        if (url && fromID && toID && mode) {
            fetch(`${url}/${fromID}/${toID}/${mode}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ _token: getMeta('csrf-token') }),
            })
                .then(async response => {
                    if (response.status !== 200) {
                        if (response.headers.get('Content-Type') === 'application/json') {
                            application.toastError(await response.json());
                        } else {
                            application.toastGeneralError();
                        }
                    }
                })
                .catch((e) => {
                    console.error(e);
                    application.toastGeneralError();
                });
        }

        this.element.parentNode.insertBefore(this.element, this.swapper);
        this.removeTemporaryElements();
        this.element.dispatchEvent(new CustomEvent('dnd:end', { bubbles: true, cancelable: true, detail: { from: fromElement, to: toElement, mode: mode } }));

        this.dropActionLoading = false;
    }

    dragMove(event) {
        if (this.animated) {
            this.animated = false;

            requestAnimationFrame(() => {
                if (this.element.dataset.dragging === 'true' && !this.dropActionLoading) {
                    this.placeholder.style.left = (event.clientX - this.placeholder.dataset.dragOffsetX) + 'px';
                    this.placeholder.style.top = (event.clientY - this.placeholder.dataset.dragOffsetY) + 'px';
                }

                this.animated = true;
            });
        }
    }

    dragOver() {
        this.element.dispatchEvent(new CustomEvent('dnd:notify-dragged-over-dropzone', { bubbles: true }));
    }

    dragOverDropzone(event) {
        if (!this.dropActionLoading && this.element.dataset.dragging === 'true' && this.element !== event.target && this.element.parentNode === event.target.parentNode) {
            if (this.swapper.nextElementSibling === event.target) {
                event.target.nextElementSibling.after(this.swapper);
                this.dropTarget = event.target;
                this.dropMode = 'after';
            } else {
                event.target.before(this.swapper);
                this.dropTarget = event.target;
                this.dropMode = 'before';
            }
        }
    }

    makeSwapper() {
        const swapper = this.element.cloneNode(true);
        swapper.removeAttribute('data-controller');
        swapper.removeAttribute('data-dragging');
        swapper.classList.add('dnd-swapper');

        return swapper;
    }

    makePlaceholder(event) {
        const boundingClientRect = this.element.getBoundingClientRect();
        const mirror = this.element.cloneNode(true);
        mirror.classList.add('dnd-placeholder');
        mirror.removeAttribute('data-controller');
        mirror.style.width = boundingClientRect.width.toFixed() + 'px';
        mirror.style.height = boundingClientRect.height.toFixed() + 'px';

        const originalChildren = this.element.querySelectorAll('*');
        const mirrorChildren = mirror.querySelectorAll('*');

        for (let i = 0; i < originalChildren.length; i++) {
            const bb = originalChildren[i].getBoundingClientRect();
            mirrorChildren[i].style.width = (bb.width + 1).toFixed() + 'px';
            mirrorChildren[i].style.height = (bb.height + 1).toFixed() + 'px';
        }

        mirror.dataset.dragOffsetX = (event.clientX - boundingClientRect.x) + '';
        mirror.dataset.dragOffsetY = (event.clientY - boundingClientRect.y) + '';
        mirror.style.left = (event.clientX - mirror.dataset.dragOffsetX) + 'px';
        mirror.style.top = (event.clientY - mirror.dataset.dragOffsetY) + 'px';

        return mirror;
    }

    removeTemporaryElements() {
        this.element.dataset.dragging = 'false';

        if (this.placeholder) {
            this.placeholder.remove();
        }

        if (this.swapper) {
            this.swapper.remove();
        }

        this.dropTarget = null;
        this.dropMode = null;

        document.body.classList.remove('dnd-block-user-select');
        document.removeEventListener('mouseup', this.mouseUp);
        document.removeEventListener('mousemove', this.mouseMove);
        this.element.parentNode.removeEventListener('dnd:notify-dragged-over-dropzone', this.mouseOverDropZone);
    }

    disconnect() {
        document.body.classList.remove('dnd-block-user-select');
        document.removeEventListener('mouseup', this.mouseUp);
        document.removeEventListener('mousemove', this.mouseMove);
        this.element.removeEventListener('mouseover', this.mouseOver);

        if (this.element.parentNode) {
            this.element.parentNode.removeEventListener('dnd:notify-dragged-over-dropzone', this.mouseOverDropZone);
        }

        if (this.swapper) {
            this.swapper.remove();
        }

        if (this.placeholder) {
            this.placeholder.remove();
        }

        super.disconnect();
    }
}
